/**
 * R-125 (5.7) — Zones come in two classes derived from the field's
 * `ocrExtracted` flag (not stored on the zone). OCR zones get the copper
 * accent + warm fill; guide-only zones get a muted slate border + cool
 * fill so the operator can see at a glance which zones feed extraction
 * vs which are keying-staff guides.
 */
export type ZoneClass = "ocr" | "guide";

interface ZoneOverlay {
  fieldId: string;
  zone: { x: number; y: number; width: number; height: number };
  isLinked: boolean;
  zoneClass: ZoneClass;
}

interface SuggestionOverlay {
  id: string;
  boundingBox: { x: number; y: number; width: number; height: number };
  status: "pending" | "linked" | "rejected";
}

interface DrawingRect {
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
}

/**
 * R-157 — Image layer parameters. Just the page bitmap + the dark surround
 * and light page sheet. Painted only when viewport / page changes — never
 * on every drawingRect mousemove. Carries no zone / overlay state so the
 * image canvas does not re-render on overlay updates.
 */
export interface ZoneImageRenderParams {
  ctx: CanvasRenderingContext2D;
  viewport: { x: number; y: number; scale: number };
  pageBitmap: ImageBitmap | null;
  pageWidth: number;
  pageHeight: number;
  canvasWidth: number;
  canvasHeight: number;
}

/**
 * R-157 — Overlay layer parameters. Zones, suggestions, selection handles,
 * and the in-progress drawing rectangle. Repaints per frame during active
 * interaction. The overlay canvas never carries the bitmap, so per-frame
 * repaints stay cheap and the underlying page image never flickers.
 */
export interface ZoneOverlayRenderParams {
  ctx: CanvasRenderingContext2D;
  viewport: { x: number; y: number; scale: number };
  pageWidth: number;
  pageHeight: number;
  /**
   * R-125 (5.1) — Canvas viewport dimensions in CSS pixels. Used to clear
   * the overlay canvas to fully transparent between frames.
   */
  canvasWidth: number;
  canvasHeight: number;
  zones: ZoneOverlay[];
  selectedZoneFieldId: string | null;
  ocrSuggestions: SuggestionOverlay[];
  drawingRect: DrawingRect | null;
  showDismissed?: boolean;
}

// 8 handle positions for a zone: 4 corners + 4 edge midpoints
function getHandlePositions(
  px: number,
  py: number,
  pw: number,
  ph: number
): [number, number][] {
  const cx = px + pw / 2;
  const cy = py + ph / 2;
  const right = px + pw;
  const bottom = py + ph;
  return [
    [px, py],         // nw
    [cx, py],         // n
    [right, py],      // ne
    [right, cy],      // e
    [right, bottom],  // se
    [cx, bottom],     // s
    [px, bottom],     // sw
    [px, cy],         // w
  ];
}

/**
 * R-157 — Render only the page image and the surrounding background. Called
 * by the back-layer image canvas; safe to call infrequently because the page
 * bitmap is the expensive part and rarely changes.
 */
export function renderZoneImageLayer(params: ZoneImageRenderParams): void {
  const { ctx, viewport, pageBitmap, pageWidth, pageHeight, canvasWidth, canvasHeight } = params;
  const { x: vpX, y: vpY, scale } = viewport;

  // R-125 (5.1) — Layer 1: fill only the visible canvas region with a soft
  // surface color. Reset transform so the fill uses raw CSS pixels.
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = "#1a1a1a";
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  // Apply viewport transform
  ctx.setTransform(scale, 0, 0, scale, vpX, vpY);

  // R-125 (5.1) — Light "page sheet" backdrop only when we know the real
  // page dimensions. Gives the operator a sense of extent while the bitmap
  // is decoding without filling the canvas dark.
  if (pageWidth > 0 && pageHeight > 0) {
    ctx.fillStyle = "#fafafa";
    ctx.fillRect(0, 0, pageWidth, pageHeight);
  }

  // Layer 2: page image if available
  if (pageBitmap) {
    ctx.drawImage(pageBitmap, 0, 0);
  }
}

/**
 * R-157 — Render the overlay layer (zones, suggestions, drawing rect,
 * selection handles) onto a transparent canvas that sits over the image
 * canvas. Called per frame during interaction. Carries no bitmap blit, so
 * per-frame cost is minimal and the underlying image never flickers.
 */
export function renderZoneOverlayLayer(params: ZoneOverlayRenderParams): void {
  const {
    ctx,
    viewport,
    pageWidth,
    pageHeight,
    canvasWidth,
    canvasHeight,
    zones,
    selectedZoneFieldId,
    ocrSuggestions,
    drawingRect,
    showDismissed = false,
  } = params;

  const { x: vpX, y: vpY, scale } = viewport;

  // Reset transform and clear the overlay canvas to fully transparent so the
  // image canvas below shows through everywhere except where we paint.
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvasWidth, canvasHeight);

  // Apply viewport transform so overlay coords match the image coords.
  ctx.setTransform(scale, 0, 0, scale, vpX, vpY);

  // Layer 3: OCR suggestions
  for (const suggestion of ocrSuggestions) {
    if (suggestion.status === "rejected" && !showDismissed) continue;

    const { x, y, width, height } = suggestion.boundingBox;
    const px = x * pageWidth;
    const py = y * pageHeight;
    const pw = width * pageWidth;
    const ph = height * pageHeight;

    ctx.fillStyle = "rgba(59, 130, 246, 0.1)";
    ctx.fillRect(px, py, pw, ph);

    ctx.strokeStyle = "#3b82f6";
    ctx.lineWidth = 1.5 / scale;
    ctx.setLineDash([6 / scale, 3 / scale]);
    ctx.strokeRect(px, py, pw, ph);
    ctx.setLineDash([]);
  }

  // R-125 (5.7) — Layer 4: persisted zones using their class colors.
  for (const overlay of zones) {
    if (overlay.fieldId === selectedZoneFieldId) continue;

    const { x, y, width, height } = overlay.zone;
    const px = x * pageWidth;
    const py = y * pageHeight;
    const pw = width * pageWidth;
    const ph = height * pageHeight;

    if (overlay.zoneClass === "ocr") {
      ctx.fillStyle = "rgba(194, 112, 64, 0.14)"; // copper accent
      ctx.fillRect(px, py, pw, ph);
      ctx.strokeStyle = "#c27040";
    } else {
      ctx.fillStyle = "rgba(100, 116, 139, 0.12)"; // slate-500 muted
      ctx.fillRect(px, py, pw, ph);
      ctx.strokeStyle = "#64748b";
    }
    ctx.lineWidth = 2 / scale;
    ctx.setLineDash([]);
    ctx.strokeRect(px, py, pw, ph);
  }

  // R-125 (5.7) — Layer 5: selected zone gets a stronger Safety Orange ring
  // regardless of class so it stands out from peers. The handles match.
  if (selectedZoneFieldId !== null) {
    const selectedOverlay = zones.find((z) => z.fieldId === selectedZoneFieldId);
    if (selectedOverlay) {
      const { x, y, width, height } = selectedOverlay.zone;
      const px = x * pageWidth;
      const py = y * pageHeight;
      const pw = width * pageWidth;
      const ph = height * pageHeight;

      ctx.fillStyle = "rgba(234, 88, 12, 0.14)";
      ctx.fillRect(px, py, pw, ph);

      ctx.strokeStyle = "#ea580c";
      ctx.lineWidth = 2 / scale;
      ctx.setLineDash([]);
      ctx.strokeRect(px, py, pw, ph);

      // 8 resize handles
      const handleRadius = 4 / scale;
      const handles = getHandlePositions(px, py, pw, ph);
      for (const [hx, hy] of handles) {
        ctx.beginPath();
        ctx.arc(hx, hy, handleRadius, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
        ctx.strokeStyle = "#ea580c";
        ctx.lineWidth = 1.5 / scale;
        ctx.stroke();
        ctx.closePath();
      }
    }
  }

  // Layer 6: in-progress rectangle
  if (drawingRect !== null) {
    const { startX, startY, currentX, currentY } = drawingRect;
    const px = Math.min(startX, currentX) * pageWidth;
    const py = Math.min(startY, currentY) * pageHeight;
    const pw = Math.abs(currentX - startX) * pageWidth;
    const ph = Math.abs(currentY - startY) * pageHeight;

    ctx.fillStyle = "rgba(34, 197, 94, 0.1)";
    ctx.fillRect(px, py, pw, ph);

    ctx.strokeStyle = "#22c55e";
    ctx.lineWidth = 1.5 / scale;
    ctx.setLineDash([6 / scale, 3 / scale]);
    ctx.strokeRect(px, py, pw, ph);
    ctx.setLineDash([]);
  }
}
